'use client';

import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.js';
import { Link } from '@sola/ark-core/atoms/link/link.js';
import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import { trackClick } from '@sola/cookies/components/datalayer.js';

import { useDonate } from '../donate-context';
import { DonateErrorObject, DonateVariants } from '../donate.types';
import { useDonateValidation } from '../hooks/use-donate-validation';

const ErrorMessage: React.FC<{
  message: string | DonateErrorObject;
}> = ({ message }) => {
  const { t } = useTranslation();

  if (typeof message === 'object') {
    return (
      <>
        {message.title && (
          <span>{`${t(message.title.key, message.title.payload)} `}</span>
        )}
        {message.message && (
          <Trans
            i18nKey={message.message.key}
            values={{ ...message.message.payload }}
            components={{
              anchor: (
                <Link
                  className="link"
                  href={`mailto:${t('contact.emailGiftPlanning')}`}
                >
                  {t('contact.emailGiftPlanning')}
                </Link>
              ),
            }}
          />
        )}
      </>
    );
  }
  return message;
};

export const DonateSubmitButton: React.FC<{ className?: string }> = ({
  className,
}) => {
  const { t } = useTranslation();
  const { state, variant } = useDonate();
  const { getCurrentAmount, validationState } = useDonateValidation();

  const donateAmount = getCurrentAmount() || '';
  const numericAmount = Number(donateAmount);

  const getButtonText = () => {
    if (state.otherSelected && numericAmount <= 0) {
      return t('skippy.donation.enterAnAmount');
    }

    if (variant === DonateVariants.grid && !state.otherSelected) {
      return t('skippy.donation.addToBasket');
    }

    return t('skippy.donation.donateAmount', {
      context: state.donateFrequency,
      amount: numericAmount,
    });
  };

  return (
    <div className={`DonateSubmitButton grid gap-snug-xl ${className || ''}`}>
      <ButtonStyle
        variant="primary"
        size="lg"
        fullWidth
        disabled={validationState.isDisabled || state.isSubmitting}
        isBusy={state.isSubmitting}
      >
        <button
          type="submit"
          onClick={() => {
            trackClick(getButtonText());
          }}
        >
          {getButtonText()}
        </button>
      </ButtonStyle>

      {validationState.message && donateAmount && (
        <Alert
          key="validation-error"
          noIcon
          message={<ErrorMessage message={validationState.message} />}
          variant="negative"
        />
      )}
    </div>
  );
};
